import { FC } from "react";
import { useTranslation } from "react-i18next";

import { formatDurationSecondsCompact, NO_VALUE_DISPLAY } from "@ryde/shared";

import {
  STATION_TIMING_DELTA_ARROWS,
  STATION_TIMING_DELTA_TONE_CLASSES,
} from "./station-timing-preview.constants";
import {
  IStationTimingDelta,
  StationTimingDeltaToneEnum,
} from "./station-timing-preview.models";
import {
  formatStationTimingDeltaPercent,
  formatStationTimingDeltaSeconds,
} from "./station-timing-preview.utils";

interface IStationTimingDeltaProps {
  /** `undefined` on a leg with nothing selected and on a leg the backend could not measure. */
  delta: IStationTimingDelta | undefined;
  /** Day lines repeat the comparison of their leg, so they carry it as plain text without the share. */
  isCompact: boolean;
}

export const StationTimingDelta: FC<IStationTimingDeltaProps> = ({
  delta,
  isCompact,
}) => {
  const { t } = useTranslation();

  if (!delta) {
    return <span className="station-timing__numeric">{NO_VALUE_DISPLAY}</span>;
  }

  const toneClassName = STATION_TIMING_DELTA_TONE_CLASSES[delta.tone];
  const compactClassName = isCompact ? " station-timing__delta--compact" : "";

  // Nothing to compare against, so the pill states the measurement itself instead of a made-up gap.
  if (
    delta.tone === StationTimingDeltaToneEnum.NoPlan ||
    delta.seconds === undefined
  ) {
    return (
      <span className="station-timing__delta-stack">
        <span
          className={`station-timing__delta ${toneClassName}${compactClassName}`}
        >
          <span className="station-timing__numeric">
            {formatDurationSecondsCompact(delta.measuredSeconds)}
          </span>
        </span>
        <span className="station-timing__delta-sub">
          {t("stationTiming.NO_PLAN_SET")}
        </span>
      </span>
    );
  }

  return (
    <span className="station-timing__delta-stack">
      <span
        className={`station-timing__delta ${toneClassName}${compactClassName}`}
      >
        {/* The glyph only repeats the sign written next to it, so it stays out of the a11y tree. */}
        <span className="station-timing__delta-arrow" aria-hidden="true">
          {STATION_TIMING_DELTA_ARROWS[delta.tone]}
        </span>
        <span className="station-timing__numeric">
          {formatStationTimingDeltaSeconds(delta.seconds)}
        </span>
      </span>
      {isCompact || delta.percent === undefined ? null : (
        <span className="station-timing__delta-sub station-timing__numeric">
          {formatStationTimingDeltaPercent(delta.percent)}
        </span>
      )}
    </span>
  );
};
